import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Sparkles, BookOpen, Image, Gauge, Languages,
  Settings, User, LogOut, Menu, X, ShieldCheck,
} from "lucide-react";

export default function Shell({ children, fullBleed = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const go = (to) => {
    setOpen(false);
    nav(to);
  };

  const linkClass = ({ isActive }) =>
    "vbs-nav-link" + (isActive ? " active" : "");

  return (
    <div className="vbs-shell">
      <div className="vbs-mobile-hdr">
        <div className="flex items-center gap-2">
          <span className="vbs-logo-dot" />
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--white)" }}>
            Viral Book Suite
          </span>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="vbs-btn vbs-btn-ghost"
          style={{ padding: "6px 10px" }}
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      <aside className={"vbs-sidebar" + (open ? " open" : "")}>
        <div className="vbs-logo" onClick={() => go("/dashboard")} style={{ cursor: "pointer" }}>
          <span className="vbs-logo-dot" />
          <div>
            <div className="vbs-logo-name">Viral Book Suite</div>
            <div className="vbs-logo-sub">Powered By The Book Cot</div>
          </div>
        </div>

        <div className="vbs-section-label">Home</div>
        <NavLink to="/dashboard" end className={linkClass} onClick={() => setOpen(false)} data-testid="nav-dashboard">
          <span className="vbs-nav-ico"><LayoutDashboard size={17} /></span>
          Dashboard
        </NavLink>

        <div className="vbs-section-label">Tools</div>
        <NavLink to="/tools/idea-evaluator" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-idea-evaluator">
          <span className="vbs-nav-ico"><Gauge size={17} /></span>
          Idea Evaluator
        </NavLink>
        <NavLink to="/tools/book-studio" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-book-studio">
          <span className="vbs-nav-ico"><BookOpen size={17} /></span>
          Book Studio
        </NavLink>
        <NavLink to="/tools/cover-studio" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-cover-studio">
          <span className="vbs-nav-ico"><Image size={17} /></span>
          Cover Studio
        </NavLink>
        <NavLink to="/tools/pubglot" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-pubglot">
          <span className="vbs-nav-ico"><Languages size={17} /></span>
          PubGlot
        </NavLink>

        <div className="vbs-section-label">Account</div>
        <NavLink to="/settings" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-settings">
          <span className="vbs-nav-ico"><Settings size={17} /></span>
          Settings
        </NavLink>
        <NavLink to="/account" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-account">
          <span className="vbs-nav-ico"><User size={17} /></span>
          My Account
        </NavLink>
        {user?.role === "admin" && (
          <NavLink to="/admin" className={linkClass} onClick={() => setOpen(false)} data-testid="nav-admin">
            <span className="vbs-nav-ico"><ShieldCheck size={17} /></span>
            Admin
          </NavLink>
        )}

        <div style={{ marginTop: "auto", paddingTop: 20, borderTop: "1px solid var(--line)" }}>
          <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--muted)", lineHeight: 1.4 }}>
            Signed in as
            <div style={{ color: "var(--bright)", fontWeight: 600, marginTop: 2 }}>
              {user?.full_name || user?.email}
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav("/login");
            }}
            className="vbs-nav-link"
            style={{ width: "100%", textAlign: "left", background: "transparent", border: "none" }}
          >
            <span className="vbs-nav-ico"><LogOut size={17} /></span>
            Logout
          </button>
        </div>
      </aside>

      {fullBleed ? (
        <div style={{ position: "relative" }}>{children}</div>
      ) : (
        <main className="vbs-main">
          {children}
          <footer
            style={{
              marginTop: 48,
              paddingTop: 20,
              borderTop: "1px solid var(--line)",
              textAlign: "center",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: 1.5,
              color: "var(--muted)",
              textTransform: "uppercase",
            }}
            data-testid="suite-footer"
          >
            Powered By{" "}
            <a
              href="https://www.thebookcot.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--gold)" }}
              data-testid="footer-book-cot-link"
            >
              The Book Cot
            </a>
          </footer>
        </main>
      )}
    </div>
  );
}
